import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Typography, Space, Form, Input, Divider, Tabs } from 'antd';
import { SafetyCertificateOutlined, LoadingOutlined, UserOutlined, LockOutlined } from '@ant-design/icons';
import { startAuthentication } from '@simplewebauthn/browser';
import { useAuthStore } from '../store/auth';
import { getLoginOptions, login, loginWithPasskey } from '../lib/api';

const { Title, Text } = Typography;

export default function Login() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('password');
  const navigate = useNavigate();
  const { setUser } = useAuthStore();

  const handlePasswordLogin = async (values: { username: string; password: string }) => {
    setLoading(true);
    setError('');

    try {
      const result = await login(values.username, values.password);
      setUser(result.user, result.token);
      navigate('/');
    } catch (err: any) {
      setError(err.message || '登入失敗，請確認帳號密碼正確');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setLoading(true);
    setError('');

    try {
      const options = await getLoginOptions();

      if (options.error) {
        throw new Error(options.error);
      }

      const challengeId = options.challengeId;
      const { challengeId: _, ...webauthnOptions } = options;

      const assertionResponse = await startAuthentication(webauthnOptions);
      const result = await loginWithPasskey(challengeId, assertionResponse);

      setUser(result.user, result.token);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Passkey 登入失敗，請確認已綁定 Passkey');
    } finally {
      setLoading(false);
    }
  };

  const passwordForm = (
    <Form form={form} layout="vertical" onFinish={handlePasswordLogin}>
      <Form.Item
        name="username"
        rules={[{ required: true, message: '請輸入帳號名' }]}
      >
        <Input
          prefix={<UserOutlined />}
          placeholder="帳號名"
          size="large"
          autoComplete="username"
        />
      </Form.Item>

      <Form.Item
        name="password"
        rules={[{ required: true, message: '請輸入密碼' }]}
      >
        <Input.Password
          prefix={<LockOutlined />}
          placeholder="密碼"
          size="large"
          autoComplete="current-password"
        />
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          loading={loading}
        >
          登入
        </Button>
      </Form.Item>
    </Form>
  );

  const passkeyForm = (
    <Space orientation="vertical" style={{ width: '100%' }} size="middle">
      <Button
        type="primary"
        size="large"
        block
        icon={loading ? <LoadingOutlined /> : <SafetyCertificateOutlined />}
        onClick={handlePasskeyLogin}
        loading={loading}
      >
        {loading ? '登入中...' : '使用 Passkey 登入'}
      </Button>

      <Text type="secondary" style={{ display: 'block', textAlign: 'center' }}>
        使用已綁定的裝置指紋、臉部辨識或 PIN 碼快速登入
      </Text>
    </Space>
  );

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '24px',
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
      >
        <Space orientation="vertical" style={{ width: '100%' }} size="large">
          <div style={{ textAlign: 'center' }}>
            <SafetyCertificateOutlined
              style={{ fontSize: 64, color: '#1890ff', marginBottom: 16 }}
            />
            <Title level={3} style={{ margin: 0 }}>
              任務分配與回報系統
            </Title>
            <Text type="secondary">請選擇登入方式</Text>
          </div>

          {error && <Alert title={error} type="error" showIcon closable />}

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            centered
            items={[
              {
                key: 'password',
                label: '密碼登入',
                children: passwordForm,
              },
              {
                key: 'passkey',
                label: 'Passkey 登入',
                children: passkeyForm,
              },
            ]}
          />

          <Divider />

          <div
            style={{
              textAlign: 'center',
              padding: '16px',
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: '8px',
            }}
          >
            <Text strong style={{ color: '#52c41a' }}>
              首次使用？
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '14px' }}>
              請使用管理員提供的註冊連結完成帳號設定
            </Text>
          </div>
        </Space>
      </Card>
    </div>
  );
}
